// src/BusDetailsCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { db, auth, rtdb } from "./firebase";
import { doc, onSnapshot, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { ref, onValue, off } from "firebase/database";
import "animate.css";

export default function BusDetailsCard({
  busId = "bus1", // "bus1".."bus15"
  title = "Bus Route Details",
  busName = "Bus",
  vehicleNo = "-",

  // fallback placeholders
  route = "-",
  timing = "-",
  driverName = "-",
  driverPhone = "-",
  driverExperience = "-",
  driverPhotoUrl = "https://cdn-icons-png.flaticon.com/512/219/219983.png",

  imageUrl = "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1e/Modern_city_bus.jpg/800px-Modern_city_bus.jpg",

  backTo = "/?buses=1",
  backLabel = "Back to Buses"
}) {
  const [busDoc, setBusDoc] = useState(null);      // ✅ Firestore (route/stops)
  const [driverDoc, setDriverDoc] = useState(null);// ✅ Firestore (driver details)
  const [liveDoc, setLiveDoc] = useState(null);    // ✅ RTDB live/{busId}

  // ✅ My Details UI
  const [showMyDetails, setShowMyDetails] = useState(false);
  const [year, setYear] = useState("");
  const [stopName, setStopName] = useState("");
  const [loadingMy, setLoadingMy] = useState(false);
  const [savingMy, setSavingMy] = useState(false);
  const [myMsg, setMyMsg] = useState({ type: "", text: "" });

  const user = auth.currentUser;

  // -----------------------------
  // 1) Firestore bus doc (route/stops/times/driverUid etc.)
  // -----------------------------
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "buses", busId), (snap) => {
      setBusDoc(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [busId]);

  // -----------------------------
  // 2) Driver doc (optional) from Firestore drivers/{uid}
  // -----------------------------
  useEffect(() => {
    const uid = busDoc?.driverUid;
    if (!uid) {
      setDriverDoc(null);
      return;
    }
    const unsub = onSnapshot(doc(db, "drivers", uid), (snap) => {
      setDriverDoc(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [busDoc?.driverUid]);

  // -----------------------------
  // 3) RTDB live doc: live/{busId}  ✅ live location only
  // -----------------------------
  useEffect(() => {
    const bid = String(busId || "").toLowerCase();
    const r = ref(rtdb, `live/${bid}`);

    const unsub = onValue(
      r,
      (snap) => setLiveDoc(snap.exists() ? snap.val() : null),
      () => setLiveDoc(null)
    );

    return () => {
      try {
        unsub();
      } catch (e) {
        try {
          off(r);
        } catch (_) {}
      }
    };
  }, [busId]);

  // -----------------------------
  // Live status + coords (RTDB)
  // -----------------------------
  const liveUpdatedMs = useMemo(() => {
    const v = liveDoc?.updatedAtMs;
    const n = typeof v === "number" ? v : Number(v || 0);
    return Number.isFinite(n) ? n : 0;
  }, [liveDoc]);

  const liveNow = useMemo(() => {
    if (!liveDoc) return false;
    if (liveDoc.isLive === false) return false;
    if (!liveUpdatedMs) return false;
    return Date.now() - liveUpdatedMs < 2 * 60 * 1000; // 2 minutes
  }, [liveDoc, liveUpdatedMs]);

  const coords = useMemo(() => {
    const lat = liveDoc?.lat;
    const lng = liveDoc?.lng;
    if (lat == null || lng == null) return null;
    return { lat: Number(lat), lng: Number(lng) };
  }, [liveDoc]);

  const canTrack = liveNow && !!coords;

  const openGoogleMaps = () => {
    if (!canTrack) {
      alert("Bus not live / location not available.");
      return;
    }
    window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, "_blank");
  };

  // -----------------------------
  // Route/Timing summary (Firestore busDoc)
  // -----------------------------
  const stops = busDoc?.stops || [];
  const times = busDoc?.times || [];

  const routeText = useMemo(() => {
    if (stops.length >= 2) return `${stops[0]} → ... → ${stops[stops.length - 1]}`;
    if (stops.length === 1) return stops[0];
    return route;
  }, [stops, route]);

  const timingText = useMemo(() => {
    if (times.length >= 2) return `${times[0]} - ${times[times.length - 1]}`;
    if (times.length === 1) return times[0];
    return timing;
  }, [times, timing]);

  const displayBusName = busDoc?.name || busName;
  const displayVehicleNo = busDoc?.vehicleNo || vehicleNo;

  const displayDriverName = driverDoc?.name || driverName;
  const displayDriverPhone = driverDoc?.phone || driverPhone;
  const displayDriverExp = driverDoc?.experience || driverExperience;
  const displayDriverEmail = busDoc?.driverEmail || driverDoc?.email || "—";

  const updatedText = liveUpdatedMs ? new Date(liveUpdatedMs).toLocaleString() : "—";

  // ✅ Load existing student details when opening form
  useEffect(() => {
    const load = async () => {
      if (!showMyDetails) return;
      if (!auth.currentUser) {
        setMyMsg({ type: "err", text: "Please login as Student to submit details." });
        return;
      }

      try {
        setLoadingMy(true);
        setMyMsg({ type: "", text: "" });

        const uid = auth.currentUser.uid;
        const snap = await getDoc(doc(db, "buses", busId, "students", uid));
        if (snap.exists()) {
          const d = snap.data() || {};
          setYear(d.year != null ? String(d.year) : "");
          setStopName(d.stopName || "");
        } else {
          setYear("");
          setStopName("");
        }
      } catch (e) {
        setMyMsg({ type: "err", text: e?.message || "Failed to load your details." });
      } finally {
        setLoadingMy(false);
      }
    };

    load();
  }, [showMyDetails, busId]);

  const saveMyDetails = async () => {
    if (!auth.currentUser) {
      setMyMsg({ type: "err", text: "Please login first." });
      return;
    }

    const y = String(year || "").trim();
    const s = String(stopName || "").trim();

    if (!y || !["1", "2", "3", "4"].includes(y)) {
      setMyMsg({ type: "err", text: "Select Year (1 to 4)." });
      return;
    }
    if (!s) {
      setMyMsg({ type: "err", text: "Enter your Stop name." });
      return;
    }

    try {
      setSavingMy(true);
      setMyMsg({ type: "", text: "" });

      const uid = auth.currentUser.uid;
      const refDoc = doc(db, "buses", busId, "students", uid);

      const existing = await getDoc(refDoc);
      const base = {
        busId,
        year: Number(y),
        stopName: s,
        updatedAt: serverTimestamp(),
        updatedByUid: uid,
        updatedByEmail: auth.currentUser.email || ""
      };

      if (!existing.exists()) {
        base.createdAt = serverTimestamp();
      }

      await setDoc(refDoc, base, { merge: true });

      setMyMsg({ type: "ok", text: "Saved. Admin/Driver will receive your details." });

      setTimeout(() => {
        setShowMyDetails(false);
        setMyMsg({ type: "", text: "" });
      }, 900);
    } catch (e) {
      setMyMsg({ type: "err", text: e?.message || "Failed to save." });
    } finally {
      setSavingMy(false);
    }
  };

  return (
    <div className="bdc-page">
      <style>{`
        .bdc-page{
          min-height:100vh;
          padding: 86px 16px 40px;
          display:flex;
          align-items:center;
          justify-content:center;
          position:relative;
          overflow:hidden;
        }
        .bdc-shell{ position:relative; z-index:1; width:100%; max-width: 980px; }

        .glass-pro{
          position: relative;
          border-radius: 22px;
          background: linear-gradient(180deg, rgba(255,255,255,.13), rgba(255,255,255,.06));
          border: 1px solid rgba(255,255,255,.18);
          box-shadow: 0 22px 70px rgba(0,0,0,.42);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          overflow: hidden;
        }
        .glass-pro::before{
          content:"";
          position:absolute;
          inset:-2px;
          background:
            radial-gradient(900px 420px at 15% 10%, rgba(47,123,255,.22), transparent 55%),
            radial-gradient(900px 420px at 90% 20%, rgba(255,77,109,.18), transparent 55%);
          opacity:.55;
          pointer-events:none;
        }
        .glass-pro > *{ position:relative; z-index:1; }

        .bdc-top{
          padding: 16px 18px;
          border-bottom: 1px solid rgba(255,255,255,.12);
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap: 12px;
        }
        .bdc-title{ color: rgba(255,255,255,.95); font-weight: 900; margin:0; }
        .bdc-sub{ color: rgba(234,240,255,.72); font-size: 13px; margin-top: 6px; }

        .bdc-close{
          width: 40px; height: 40px;
          border-radius: 14px;
          display:flex; align-items:center; justify-content:center;
          border: 1px solid rgba(255,255,255,.18);
          background: rgba(255,255,255,.08);
          color: rgba(255,255,255,.92);
          text-decoration:none;
          font-weight: 900;
          line-height: 1;
        }
        .bdc-close:hover{ background: rgba(255,255,255,.14); }

        .bdc-body{ padding: 18px; }

        .bdc-bus-img{
          width:100%;
          height: 280px;
          object-fit: cover;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,.16);
        }

        .pill{
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(255,255,255,.07);
          border-radius: 14px;
          padding: 12px 12px;
          color: rgba(255,255,255,.90);
          height: 100%;
        }
        .label{ color: rgba(234,240,255,.70); font-size: 12px; }
        .value{ color: rgba(255,255,255,.95); font-weight: 900; }

        .divider{ border-top: 1px solid rgba(255,255,255,.12); margin: 16px 0; }

        .driver-box{ display:flex; align-items:center; gap: 12px; }
        .driver-photo{
          width: 56px; height: 56px; border-radius: 16px;
          object-fit: cover; border: 1px solid rgba(255,255,255,.18);
        }

        .btn-soft{
          border-radius: 14px !important;
          font-weight: 900 !important;
          padding: 10px 14px !important;
        }

        .btn-track-live{
          border: 0 !important;
          color: #071b10 !important;
          background: linear-gradient(135deg, rgba(21,199,119,1), rgba(34,197,94,1)) !important;
          box-shadow: 0 14px 38px rgba(21,199,119,.28);
        }
        .btn-track-off{
          border: 1px solid rgba(255,77,109,.35) !important;
          background: rgba(255,77,109,.12) !important;
          color: rgba(255,230,235,.95) !important;
          font-weight: 900 !important;
        }

        .status-pill{
          display:inline-flex;
          align-items:center;
          padding: 6px 10px;
          border-radius: 12px;
          font-weight: 900;
          letter-spacing:.6px;
          border: 1px solid rgba(255,255,255,.16);
          background: rgba(255,255,255,.08);
          color: rgba(255,255,255,.92);
        }

        .my-wrap{
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(255,255,255,.07);
          border-radius: 16px;
          padding: 14px;
        }
        .my-title{ color: rgba(255,255,255,.95); font-weight: 900; margin: 0 0 8px 0; }
        .my-help{ color: rgba(234,240,255,.72); font-size: 12px; margin-bottom: 12px; line-height: 1.6; }
        .my-input{
          width: 100%;
          border-radius: 14px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          color: rgba(255,255,255,.92);
          padding: 10px 12px;
          outline: none;
        }
        .my-msg{
          margin-top: 10px;
          border-radius: 12px;
          padding: 10px 12px;
          font-weight: 900;
          font-size: 13px;
        }
        .my-msg.ok{
          border: 1px solid rgba(21,199,119,.35);
          background: rgba(21,199,119,.12);
          color: rgba(234,255,246,.95);
        }
        .my-msg.err{
          border: 1px solid rgba(255,77,109,.35);
          background: rgba(255,77,109,.12);
          color: rgba(255,230,235,.95);
        }
        .my-msg.info{
          border: 1px solid rgba(255,193,7,.30);
          background: rgba(255,193,7,.12);
          color: rgba(255,255,255,.95);
        }
      `}</style>

      <div className="bdc-shell wow animate__animated animate__fadeInUp animate__slow">
        <div className="glass-pro">
          <div className="bdc-top">
            <div>
              <h3 className="bdc-title">{title}</h3>
              <div className="bdc-sub">
                {displayBusName} • {liveNow ? "LIVE" : "OFFLINE"}
              </div>
            </div>

            <Link className="bdc-close" to={backTo} aria-label="Close">
              ✕
            </Link>
          </div>

          <div className="bdc-body">
            {showMyDetails ? (
              <div className="my-wrap">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <h4 className="my-title">My Details</h4>
                    <div className="my-help">
                      Submit <b>Year</b> and <b>Stop</b>. Only Admin/Driver/Incharge can view.
                    </div>
                  </div>

                  <button
                    className="btn btn-outline-light btn-soft"
                    onClick={() => {
                      setShowMyDetails(false);
                      setMyMsg({ type: "", text: "" });
                    }}
                  >
                    Close
                  </button>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-4">
                    <div className="label mb-2">Year</div>
                    <select
                      className="my-input"
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      disabled={loadingMy || savingMy}
                    >
                      <option value="">Select Year</option>
                      <option value="1">1st Year</option>
                      <option value="2">2nd Year</option>
                      <option value="3">3rd Year</option>
                      <option value="4">4th Year</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-8">
                    <div className="label mb-2">Stop Name</div>
                    <input
                      className="my-input"
                      value={stopName}
                      onChange={(e) => setStopName(e.target.value)}
                      placeholder="Enter your boarding stop"
                      disabled={loadingMy || savingMy}
                    />
                  </div>
                </div>

                <div className="d-flex gap-2 flex-wrap mt-3">
                  <button
                    className="btn btn-success btn-soft"
                    onClick={saveMyDetails}
                    disabled={savingMy || loadingMy || !user}
                  >
                    {savingMy ? "Saving..." : "Submit"}
                  </button>

                  <button
                    className="btn btn-outline-light btn-soft"
                    onClick={() => {
                      setYear("");
                      setStopName("");
                    }}
                    disabled={savingMy || loadingMy}
                  >
                    Clear
                  </button>
                </div>

                {loadingMy && <div className="my-msg info">Loading your saved details...</div>}
                {myMsg.text && <div className={`my-msg ${myMsg.type}`}>{myMsg.text}</div>}
              </div>
            ) : (
              <>
                <img src={imageUrl} alt={`${displayBusName} bus`} className="bdc-bus-img mb-3" />

                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <div className="pill">
                      <div className="label">Bus</div>
                      <div className="value">{displayBusName}</div>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="pill">
                      <div className="label">Vehicle No</div>
                      <div className="value">{displayVehicleNo}</div>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="pill">
                      <div className="label">Live Last Updated</div>
                      <div className="value" style={{ fontSize: 13, fontWeight: 800 }}>
                        {updatedText}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="divider" />

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="pill">
                      <div className="label">Route (summary)</div>
                      <div className="value">{routeText}</div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="pill">
                      <div className="label">Timing (summary)</div>
                      <div className="value">{timingText}</div>
                    </div>
                  </div>
                </div>

                <div className="divider" />

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="pill">
                      <div className="label">Driver</div>
                      <div className="driver-box mt-2">
                        <img className="driver-photo" src={driverPhotoUrl} alt="Driver" />
                        <div>
                          <div className="value">{displayDriverName}</div>
                          <div className="label mt-1">
                            Phone: {displayDriverPhone} • Exp: {displayDriverExp}
                          </div>
                          <div className="label mt-1">Email: {displayDriverEmail}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="pill">
                      <div className="label">Live Status</div>
                      <div className="mt-2">
                        <span className="status-pill">
                          {liveNow ? "LIVE (RTDB)" : "OFFLINE"}
                        </span>
                        <div className="label mt-2">
                          Lat: {coords ? coords.lat.toFixed(6) : "—"} • Lng:{" "}
                          {coords ? coords.lng.toFixed(6) : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="divider" />

                <div className="d-flex gap-2 flex-wrap">
                  <Link to={backTo} className="btn btn-outline-light btn-soft">
                    ⬅ {backLabel}
                  </Link>

                  <button
                    className={`btn btn-soft ${canTrack ? "btn-track-live" : "btn-track-off"}`}
                    onClick={openGoogleMaps}
                    disabled={!canTrack}
                  >
                    {canTrack ? "Track Live (Maps)" : "Not Live"}
                  </button>

                  <button
                    className="btn btn-outline-light btn-soft"
                    onClick={() => {
                      if (!auth.currentUser) {
                        alert("Please login as Student first.");
                        return;
                      }
                      setShowMyDetails(true);
                    }}
                  >
                    My Details
                  </button>
                </div>

                <div className="mt-3" style={{ color: "rgba(234,240,255,.65)", fontSize: 12 }}>
                  Tip: Driver details can also be stored in <b>drivers/{`{uid}`}</b> document
                  (name/phone/experience).
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}